import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormGroup} from '@angular/forms';
import { Subscription } from 'rxjs';

import { CustomEventService } from '@shared/services/custom-event.service';
import { GlobalSettingsService } from '@shared/services/global-settings.service';

import ActionTypesEnum from '@shared/enums/action-constant.enum';

import DateUtil from '@shared/utils/date.util';
import {
  createFormGroupWithKey,
  requestObjectMapper,
  updateFormValidators,
  validateFormGroup
} from '@shared/utils/form.util';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { differenceInCalendarDays } from 'date-fns';
import { PropertiesService } from "@core/services/properties.service";

@UntilDestroy()
@Component({
  selector: 'app-add-widget-modal',
  templateUrl: './add-widget-modal.component.html'
})
export class AddWidgetModalComponent implements AfterViewInit, OnInit, OnDestroy {
  LABEL_LENGTH = 10;

  @Input() fields: Widget.Field[];
  @Input() header;
  @Input() mode;
  @Input() loading: boolean;
  @Input() id;
  @Input() scopeId?: string;
  @Input() displayType?: string;
  @Input() dateFilterable?: boolean;
  @Input() custom?: boolean;

  @ViewChild('chartInput') chartInput;

  addForm: FormGroup;
  multiProject = false;
  customWidget = {
    name: 'Custom',
    id: 'CUSTOM',
    enabled: false
  };
  selectedCustom = false;
  metrics;
  dropdownToggle = {};

  dateFormat: string;

  selectedFields: Map<string, any> = new Map();

  // thresholds
  thresholdIndicator = '>';
  thresholdMetric: string;
  thresholdValue = '0';
  thresholdStatus: string;
  thresholdStatusModel = [
    {
      key: 'SUCCESS',
      label: 'Success'
    },
    {
      key: 'ERROR',
      label: 'Error'
    }
  ];

  selectedThresholds = [];
  isSameMetric: boolean;
  resetInputs = false;
  customEventSubscription: Subscription;

  isNextDay = (date: Date) => differenceInCalendarDays(date, new Date()) > 0;
  disabledDate = (current: Date): boolean => this.isNextDay(current);


  constructor(private customEventService: CustomEventService,
              public propertiesService: PropertiesService,
              private globalSettingsService: GlobalSettingsService) { }

  // sets selectedFields Map data with each field that comes with pre selections ( especially when editing the widget )
  setSelectedFieldData = (fields: any[] = []): void => {
    fields.forEach(field => {
      let value = null;
      const selections = field.data.selections;
      if (selections) {
        value = selections instanceof Array ?
          selections.map(item => item.key) :
          selections instanceof Object ? selections.key : selections;

        if (field.type === 'date') {
          value = new Date(value);
        }

        if (field.type === 'threshold') {
          field.data.selections = field.data.selections.map(item => {
            return {
              ...item,
              displayMetric: field.thresholds?.find(threshold => threshold.key === item.key)?.label
            }
          });
          this.selectedThresholds = field.data.selections;
        }
      }

      this.selectedFields.set(field.key, value);
    });
  };

  // converts arrays to single items where backend sets multiSelect false
  modifyRequestData(data: any): any {
    for (const key of Object.keys(data)) {
      const fieldItem = this.fields.find(item => item.key === key);

      if(!fieldItem.multiSelect && fieldItem.type === 'dropdown') {
        if(data[key] instanceof Array) {
          data[key] = data[key].reduce((prev, current) => current);
        }
      }
    }

    return data;
  }

  ngOnInit(): void {
    this.customEventSubscription = this.customEventService.eventAnnounced$
      .pipe(untilDestroyed(this)).subscribe(
        ({ action, data }) => {
          if (action === ActionTypesEnum.WIDGET_CREATE_ONE_ITEM_SELECTED) {
            this.selectField(data.event, data.field);
          }
        });
    this.dateFormat = this.globalSettingsService.dateFormat;

    this.loadInitialData();
  }

  ngAfterViewInit(): void {
    const periodItem = this.fields.find(item => item.key === 'period');

    if (periodItem) {
      if (periodItem.data.selections && 'key' in periodItem.data.selections) {
        const selection = periodItem.data.selections.key;

        setTimeout(() => {
          this.addForm.get('period').patchValue(selection);
        }, 100)
      }
    }
  }

  loadInitialData(): void {
    if(this.fields) {
      this.fields.forEach(field => {

        if (field.key !== 'effort') {
          field.required = field.validators.find(item => item === 'required');

        } else if (field.key === 'effort') {
          if (!field.validators.includes('required')) {
            field.validators.push('required');
          }
        }

        if (field.affects?.length) {
          field.affects.forEach(affect => {
            const foundField = this.fields.find(item => item.key === affect);

            if (foundField && foundField.validators.indexOf('invisible') > -1) {
              field.changeVisibilityOfFields = true;
              foundField.invisible = !(field.data.selections
                && (Array.isArray(field.data.selections) && field.data.selections.length) && field.data.selections.some(item => item.key === foundField.key));
            }
          });
        }

        const regex = field.validators.find(item => item.startsWith('regex::'));

        if (regex) {
          field.regex = regex.substring(7, regex.length);
        }

        // If there is an option that can be selected, it is automatically selected.

        if (field.data?.content?.length === 1 && !field.data?.selections && !field.affects.length) {
          const selection = {key: field.data.content[0].key};
          field.data.selections = field.multiSelect ? [selection] : selection;
        }

      });
      this.addForm = createFormGroupWithKey(this.fields);
      this.setSelectedFieldData(this.fields);

      const controlData = this.fields[0].data.content && this.fields[0].data.content.length === 1 && !this.fields[0].data.selections;

      if (controlData) {
        this.addForm.get(this.fields[0].key).setValue(this.fields[0].data.content[0].key);
        this.selectField(this.fields[0].data.content[0].key, this.fields[0]);
      }
    }
  }

  submitForm(): void {
    validateFormGroup(this.addForm);



    if (this.addForm.get('workMetricType')?.value ==='count') {
      this.addForm.removeControl("effort");
    }

    if (this.addForm.invalid) {
      return;
    }

    let requestData: any = {
      formData: {
        ...this.addForm.value,
        thresholds: this.selectedThresholds
      }
    };

    if (!this.selectedThresholds.length) {
      delete requestData.formData.thresholds
    }

    if (requestData.formData.from) {
      requestData.formData = {...requestData.formData, from: +DateUtil.formatDate(requestData.formData.from)}
    }

    requestData.formData = this.modifyRequestData(requestData.formData);

    requestData.formData = requestObjectMapper(requestData.formData, this.fields);

    for (const key of Object.keys(requestData.formData)) {
      if(requestData.formData[key] && !(requestData.formData[key] instanceof Array)
        && key !== 'from' && !requestData.formData[key].key) {
        requestData.formData[key] = {
          key: requestData.formData[key],
          label: requestData.formData[key]
        };
      }
    }

    if (this.mode === 'edit') {
      requestData = {...requestData, id: this.id};
    }

    if (this.fields.some(item => item.changeVisibilityOfFields)) {
      const removingFields = this.fields.filter(item => item.invisible && item.invisible === true);

      if (removingFields?.length) {
        Object.keys(requestData.formData).forEach(key => {
          if (removingFields.some(item => item.key === key)) {
            delete requestData.formData[key];
          }
        });
      }
    }

    if (this.custom) {
      requestData.formData = {
        ...requestData.formData,
        displayType: { key:this.displayType, label:this.displayType },
        dateFilterable: { key:this.dateFilterable, label: this.dateFilterable }
      }
    }

    this.customEventService.announceEvent(this.mode === 'add' ? ActionTypesEnum.CREATE_WIDGET : ActionTypesEnum.UPDATE_WIDGET, requestData);
  }



  cancelForm(): void {
    this.customEventService.announceEvent(ActionTypesEnum.CLOSE_MODAL);

    if (this.scopeId) {
      this.customEventService.announceEvent(ActionTypesEnum.BACK_TO_SCOPE_MODAL, { scopeId: this.scopeId });
    }
  }

  selectField(value, field): void {
    this.resetInputs = false;
    if (field.key && field.affects && value && field.affects.some(item => value.indexOf(item) > -1)) {
      field.changeVisibilityOfFields = true;
      this.updateInvisibleKeyOfFields(value);
      if (this.addForm.get('workMetricType')?.value !=='effort') {
        this.addForm = updateFormValidators(this.fields, this.addForm);
      } else if (this.addForm.get('workMetricType')?.value === 'effort') {
        if (!field.validators.includes('required')) {
          field.validators.push('required');
        }
      }

    }

    if (field.changeVisibilityOfFields && value && !value.length) {
      this.updateInvisibleKeyOfFields();
    }

    this.selectedFields.set(field.key, value);
      this.customEventService.announceEvent(ActionTypesEnum.WIDGET_CREATE_FIELD_SELECTED, {field, form: this.addForm, value});
  }

  updateInvisibleKeyOfFields(value?): void {
    this.fields.forEach(item => {
      item.invisible = item.invisible !== undefined ? (value ? value.indexOf(item.key) === -1 : true) : undefined;
    });
  }

  customWidgetClick(): void {
    this.selectedCustom = !this.selectedCustom;

    if (!this.customWidget.enabled) {
      this.customWidget.enabled = true;
    }
  }

  chartChanged($event): void {
    this.addForm.get('metrics').setValue(null);
    this.metrics = $event.metrics;
  }

  addAll(field: any): void {
    const values = field.data.content.map(item => item.key);
    this.dropdownToggle[field.key] = false;
    this.addForm.get(field.key).setValue(values);
    this.selectField(values, field);
  }

  clickedFormElement(field): void {
    if (field.infoMessage) {
      field.infoMessage.show = true;
    }
  }

  deleteThreshold(item): void {
    this.selectedThresholds = this.selectedThresholds.filter(data => data.value !== item.value || data.key !== item.key || data.label !== item.label);

    this.metricControl();
  }

  metricControl($event?: any): void {
    this.isSameMetric = this.selectedThresholds.some(item => item.key === $event);
  }

  addThreshold(): void {
    if(this.isSameMetric) {
      return;
    }

    this.addForm.get('thresholds').setValue([{
      value: this.thresholdValue,
      key: this.thresholdMetric,
      label: this.thresholdStatus,
      displayMetric: this.fields.find(field => field.type === 'threshold')?.thresholds.find(threshold => threshold.key === this.thresholdMetric)?.label,
      indicator: this.thresholdIndicator
    }]);

    const { label, value, indicator, key,  displayMetric } = this.addForm.get('thresholds').value[0];

    if (label || value || key || indicator || displayMetric) {

      this.selectedThresholds.push({ label, value, key, indicator,  displayMetric })
    }

    this.thresholdValue = '0';
    this.thresholdMetric = null;
    this.thresholdStatus = null;
  }

  searchSelect(value: string, field: any) {
      this.resetInputs = false;

      console.log("value", field);

      const foundValue=  value.length > 0 ?
      field.data.contentAll.filter(item => item.label.toLocaleLowerCase().includes(value.toLocaleLowerCase())).slice(0, 500)
      : field.data.contentAll ? field.data.contentAll.slice(0, 500) : field.data.content;


      if(foundValue && foundValue.length>0){
        field.data.content=foundValue
        return;
      }
      if (field?.widgetFieldListener?.eventType !== 'KEYUP') {
        return;
      }

      field.loading = false;
      if (field?.widgetFieldListener?.eventType !== 'KEYUP') {
        return;
      }
      if (value.length < 3) {  field.data.content = field.data.defaultContent; return; }


      this.customEventService.announceEvent(ActionTypesEnum.ANALYSIS_CREATE_FIELD_KEYUP_ACTION, {field, form: this.addForm, value});
  }

  resetForm(): void {
    const predefinedValues = this.addForm.get('predefinedWidget').value;
    const isDisabled = this.fields.filter(item => item.key === 'predefinedWidget').some(data => data?.disabled);
    this.addForm.reset();
    if (isDisabled) {
      this.addForm.get('predefinedWidget').setValue(predefinedValues);
    }
    this.resetInputs = true;
  }
  ngOnDestroy(): void {
    // prevent memory leak when component destroyed
  }
}
